'use client';

import React, { useState } from 'react';

interface Review {
  id: number;
  property: string;
  location: string;
  image: string;
  imageAlt: string;
  date: string;
  rating: number;
  comment: string;
  hostReply?: string;
  pending?: boolean;
}

const reviews: Review[] = [
  {
    id: 1,
    property: 'Safari Lodge Murchison',
    location: 'Murchison Falls, Uganda',
    image: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=80&h=80&fit=crop',
    imageAlt: 'Safari lodge at Murchison Falls National Park',
    date: 'August 15, 2026',
    rating: 5,
    comment: 'Absolutely breathtaking experience! Woke up to elephants outside our window. David was an exceptional host — arranged private game drives and the food was incredible. Will definitely return.',
    hostReply: 'Thank you so much, Sarah! It was a pleasure hosting you. The elephants loved your visit too! 🐘',
  },
  {
    id: 2,
    property: 'Mountain Cabin Rwenzori',
    location: 'Kasese, Uganda',
    image: 'https://images.unsplash.com/photo-1542718610-a1d656d1884c?w=80&h=80&fit=crop',
    imageAlt: 'Cozy mountain cabin with Rwenzori mountain views',
    date: 'June 2, 2026',
    rating: 4,
    comment: 'Beautiful cabin with stunning mountain views. The hiking trails were well-marked and the cabin was cozy. Only minor issue was the hot water was inconsistent, but Ruth was quick to fix it.',
  },
];

const pendingReviews = [
  {
    id: 3,
    property: 'Lakeside Retreat Entebbe',
    location: 'Entebbe, Uganda',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=80&h=80&fit=crop',
    imageAlt: 'Lakeside retreat with Lake Victoria views in Entebbe',
    checkOut: 'Sep 8, 2026',
  },
];

export default function GuestReviews() {
  const [expandedReply, setExpandedReply] = useState<number | null>(null);
  const [pendingRating, setPendingRating] = useState(0);
  const [pendingComment, setPendingComment] = useState('');

  return (
    <div>
      {/* Pending Reviews */}
      {pendingReviews.length > 0 && (
        <div className="mb-8">
          <h2 className="text-base font-bold text-gray-900 mb-4">Awaiting Your Review</h2>
          {pendingReviews.map((p) => (
            <div key={p.id} className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-4 mb-4">
                <img src={p.image} alt={p.imageAlt} className="w-14 h-14 rounded-xl object-cover" />
                <div>
                  <h3 className="font-bold text-gray-900">{p.property}</h3>
                  <p className="text-sm text-gray-500">{p.location} · Checked out {p.checkOut}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-gray-700">Your rating:</span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setPendingRating(star)}
                    className={`text-2xl transition-transform hover:scale-110 ${star <= pendingRating ? 'text-amber-400' : 'text-gray-300'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                value={pendingComment}
                onChange={(e) => setPendingComment(e.target.value)}
                placeholder="Share your experience with future guests..."
                className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                rows={3}
              />
              <button
                disabled={!pendingRating || !pendingComment.trim()}
                className="mt-3 px-6 py-2.5 bg-[#4088C6] disabled:bg-gray-200 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl hover:bg-[#3070a6] transition-colors"
              >
                Submit Review
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Past Reviews */}
      <h2 className="text-base font-bold text-gray-900 mb-4">Your Reviews ({reviews.length})</h2>
      <div className="space-y-5">
        {reviews.map((review) => (
          <div key={review.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-start gap-4">
              <img src={review.image} alt={review.imageAlt} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-900">{review.property}</h3>
                    <p className="text-sm text-gray-500">{review.location}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{review.date}</span>
                </div>
                <div className="flex items-center gap-0.5 my-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className={`text-base ${s <= review.rating ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{review.comment}</p>
                {review.hostReply && (
                  <div className="mt-3">
                    <button
                      onClick={() => setExpandedReply(expandedReply === review.id ? null : review.id)}
                      className="text-xs font-semibold text-[#4088C6] hover:text-[#3070a6] transition-colors"
                    >
                      {expandedReply === review.id ? '▲ Hide host reply' : '▼ Host replied'}
                    </button>
                    {expandedReply === review.id && (
                      <div className="mt-2 bg-gray-50 rounded-xl p-3 border-l-2 border-[#4088C6]/40">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Host response:</p>
                        <p className="text-sm text-gray-600">{review.hostReply}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
